import React, { useState } from "react";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useLocation } from "react-router-dom";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  FormControl,
  Card,
  Box,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { durationType } from "../initialData/initialData";
import {
  getHumanReadableDiffInPastDateWithAgo,
  getHumanReadableDurationInDate,
} from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";
import DurationAutocompleteField from "./DurationAutocompleteField";
import {
  durationUnitMap,
  normalizeLegacyDuration,
  parseCombinedDuration,
} from "./durationUtils";

const AddictionHistory = ({
  addictionHistoryData,
  setAddictionHistoryData,
  dateForAppointment,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  const location = useLocation();
  console.log("Addiction History called", addictionHistoryData);
  const today = dayjs(dateForAppointment);
  const [addictionVars, setAddictionVars] = useState({
    addiction_description: "",
    addiction_type: "",
    onset_datetime: null,
    abatement_datetime: null,
    onset_duration: "",
    abatement_duration: "",
  });
  const [addictionEditIndex, setAddictionEditIndex] = useState(null);
  const [addictionError, setAddictionError] = useState("");
  const [listeningOn, setListeningOn] = useState(false);
  const hasPendingDurationSelection = (durationValue) =>
    /^\d+$/.test(String(durationValue || "").trim());
  console.log("addictionVars", addictionVars);
  const handleAddAddictionRow = () => {
    if (
      hasPendingDurationSelection(addictionVars.onset_duration) ||
      hasPendingDurationSelection(addictionVars.abatement_duration)
    ) {
      setAddictionError("Please select a duration");
      return;
    }
    if (addictionVars?.addiction_description && addictionVars?.addiction_type) {
      // Store the updated state in a local variable
      console.log("New Addiction row", addictionVars);
      const newRow = {
        addiction_description: addictionVars.addiction_description,
        addiction_type: addictionVars.addiction_type,
        onset_datetime: addictionVars?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          addictionVars?.abatement_datetime?.format("YYYY-MM-DD"),
        onset_duration: addictionVars?.onset_duration,
        abatement_duration: addictionVars?.abatement_duration,
      };

      // Update family history rows by adding the new row

      const updatedRows = [...addictionHistoryData["new"], newRow];

      setAddictionHistoryData({
        ...addictionHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setAddictionVars({
        addiction_description: "",
        addiction_type: "",
        onset_datetime: null,
        abatement_datetime: null,
        onset_duration: "",
        abatement_duration: "",
      });
      setAddictionError("");
    } else {
      // Set error message if any field is missing
      setAddictionError("Addiction Type and Addiction Description is required");
    }
  };
  const handleAddictionEditClick = (row, rowIndex) => {
    setAddictionVars({
      addiction_description: row?.addiction_description,
      addiction_type: row?.addiction_type,
      onset_datetime: row?.onset_datetime ? dayjs(row?.onset_datetime) : null,
      abatement_datetime: row?.abatement_datetime
        ? dayjs(row?.abatement_datetime)
        : null,
      onset_duration: normalizeLegacyDuration(
        row?.onset_duration,
        row?.onset_duration,
        row?.onset_duration_type
      ),
      abatement_duration: normalizeLegacyDuration(
        row?.abatement_duration,
        row?.abatement_duration,
        row?.abatement_duration_type
      ),
    });
    setAddictionEditIndex(rowIndex);
  };
  const handleAddictionUpdate = () => {
    if (
      hasPendingDurationSelection(addictionVars.onset_duration) ||
      hasPendingDurationSelection(addictionVars.abatement_duration)
    ) {
      setAddictionError("Please select a duration");
      return;
    }
    if (addictionVars.addiction_description && addictionVars.addiction_type) {
      // Store the updated state in a local variable
      const updatedRow = {
        addiction_description: addictionVars?.addiction_description,
        addiction_type: addictionVars?.addiction_type,
        onset_datetime: addictionVars?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          addictionVars?.abatement_datetime?.format("YYYY-MM-DD"),
        onset_duration: addictionVars?.onset_duration,
        abatement_duration: addictionVars?.abatement_duration,
      };
      const updatedAddiction = addictionHistoryData["new"].map((row, index) => {
        if (index === addictionEditIndex) {
          return updatedRow;
        } else {
          return row;
        }
      });
      setAddictionHistoryData({
        ...addictionHistoryData,
        new: updatedAddiction,
      });
      // Reset family history variables and clear error
      setAddictionVars({
        addiction_description: "",
        addiction_type: "",
        onset_datetime: null,
        abatement_datetime: null,
        onset_duration: "",
        abatement_duration: "",
      });
      setAddictionEditIndex(null);
      setAddictionError("");
    } else {
      // Set error message if any field is missing
      setAddictionError("Addiction Type and Addiction Description is required");
    }
  };
  const handleAddictionDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedAddiction = addictionHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setAddictionHistoryData({
      ...addictionHistoryData,
      new: updatedAddiction,
    });
  };
  const handleChangeAddiction = (field, value) => {
    let updatedDetails = { ...addictionVars, [field]: value };

    if (field === "onset_duration") {
      const { value: onsetValue, unit: onsetUnit } = parseCombinedDuration(
        updatedDetails.onset_duration
      );
      if (onsetValue && onsetUnit) {
        const calculatedDate = calculateOnsetDate(parseInt(onsetValue, 10), onsetUnit);
        updatedDetails.onset_datetime = calculatedDate ?? null;
      } else {
        updatedDetails.onset_datetime = null;
      }
    }

    if (field === "abatement_duration") {
      const { value: abatementValue, unit: abatementUnit } = parseCombinedDuration(
        updatedDetails.abatement_duration
      );
      if (abatementValue && abatementUnit) {
        const calculatedDate = calculateAbatementDate(
          updatedDetails.onset_datetime,
          parseInt(abatementValue, 10),
          abatementUnit
        );
        updatedDetails.abatement_datetime = calculatedDate ?? null;
      } else {
        updatedDetails.abatement_datetime = null;
      }
    }

    setAddictionVars(updatedDetails);
  };
  const handleClearAddictionDescription = () => {
    setAddictionVars({
      ...addictionVars,
      addiction_description: "", // Use computed property name to set the field dynamically
    });
  };
  const calculateOnsetDate = (duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration))
    )
      return null;
    const today = dayjs(dateForAppointment); // appointment date
    console.log("date onset", today.subtract(duration, durationUnitMap[type]));
    return today.subtract(duration, durationUnitMap[type]);
  };
  const calculateAbatementDate = (onset_datetime, duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration)) ||
      !onset_datetime
    )
      return null;
    console.log(
      "date abatement",
      dayjs(onset_datetime).add(duration, durationUnitMap[type])
    );
    return dayjs(onset_datetime).add(duration, durationUnitMap[type]);
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={3} sx={{ margin: "5px 0px" }}>
          {/* <FormControl fullWidth> */}
          {/* <StyledInputLabel>Select Addiction Type</StyledInputLabel> */}
          <StyledTextField
            select
            label="Select Addiction Type"
            value={addictionVars.addiction_type}
            onChange={(e) =>
              handleChangeAddiction("addiction_type", e.target.value)
            }
            fullWidth
          >
            {["Tobacco Smoking", "Tobacco Chewing", "Alcohol", "Drugs"].map(
              (addiction) => (
                <MenuItem
                  key={addiction}
                  value={addiction}
                  sx={{ fontSize: "12px" }}
                >
                  {addiction}
                </MenuItem>
              )
            )}
          </StyledTextField>
          {/* </FormControl> */}
        </Grid>
        <Grid item xs={11} sm={8} sx={{ margin: "5px 0px" }}>
          <FormControl fullWidth>
            <StyledTextField
              label="Addiction Description"
              value={addictionVars.addiction_description}
              onChange={(e) =>
                handleChangeAddiction("addiction_description", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ marginRight: "5px" }}>
                      <Tooltip title="Clear">
                        <OutlinedIconFab
                          size="small"
                          variant="contained"
                          onClick={handleClearAddictionDescription}
                        >
                          <Icon name="clearTextField" size={32} />
                        </OutlinedIconFab>
                      </Tooltip>
                    </div>

                    <>
                      {isAiEnabled &&
                        location.pathname.includes("DoctorConsole") &&
                        (listeningOn == false ? (
                          <Tooltip title="Start Listening">
                            <OutlinedIconFab
                              // onClick={startListening}
                              onClick={() => {
                                startRecording();
                                setListeningOn(true);
                              }}
                              variant="contained"
                              color="defaultColor"
                              size="small"
                            >
                              <Icon name="micNotInUse" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Stop Listening">
                            <OutlinedIconFab
                              onClick={() => {
                                // SpeechRecognition.stopListening();
                                stopRecording("Addiction History");
                                setListeningOn(false);
                              }}
                              variant="outlined"
                              color="defaultColor"
                              size="small"
                            >
                              <Icon name="micInUse" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        ))}
                    </>
                  </div>
                ),
              }}
            />
          </FormControl>
        </Grid>

        <Grid container item xs={12} spacing={1} alignItems="center">
          <Grid
            item
            xs={11}
            sm={11}
            md={5}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Tooltip title="How long ago the addiction started">
              <Typography
                component="span"
                variant="body2"
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                Addiction Onset:
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 180, md: 200 } }}>
              <DurationAutocompleteField
                label="Duration"
                value={addictionVars.onset_duration || ""}
                onChange={(combined) =>
                  handleChangeAddiction("onset_duration", combined)
                }
                durationType={durationType}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={11}
            sm={11}
            md={6}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Tooltip title="How long the person has been addicted">
              <Typography
                component="span"
                variant="body2"
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                Addiction Duration:
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 180, md: 200 } }}>
              <DurationAutocompleteField
                label="Duration"
                value={addictionVars.abatement_duration || ""}
                onChange={(combined) =>
                  handleChangeAddiction("abatement_duration", combined)
                }
                durationType={durationType}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={11}
            sm={11}
            md={2}
            sx={{
              margin: "5px 0px",
              display: "flex",
              justifyContent: { xs: "stretch", md: "flex-end" },
              padding: "0px",
            }}
          >
            <StyledButton
              bgColor="#0E3F66"
              variant="contained"
              sx={{ width: { xs: "100%" }, marginLeft: { md: "auto" } }}
              onClick={
                addictionEditIndex !== null
                  ? handleAddictionUpdate
                  : handleAddAddictionRow
              }
              color="defaultColor"
            >
              {addictionEditIndex !== null ? "Update" : "Add"}
            </StyledButton>
          </Grid>
        </Grid>

        <Grid item xs={11} md={3} sx={{ margin: "5px 0px", display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Start Date"
                onChange={(date) =>
                  handleChangeAddiction("onset_datetime", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  addictionVars?.onset_datetime
                    ? dayjs(addictionVars?.onset_datetime)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={11} md={3} sx={{ margin: "5px 0px", display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                fullWidth
                label="End Date"
                onChange={(date) =>
                  handleChangeAddiction("abatement_datetime", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                minDate={dayjs(addictionVars.onset_datetime)}
                value={
                  addictionVars?.abatement_datetime
                    ? dayjs(addictionVars?.abatement_datetime)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {addictionError && (
            <Typography style={{ color: "red" }}>{addictionError}</Typography>
          )}
        </Grid>

        <Grid item xs={11} sm={11}>
          {addictionHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Addiction Type
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Addiction Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {addictionHistoryData["prev"].map((row, rowIndex) => {
                    // console.log("Addiction Row", row); // Logging the row object

                    return (
                      <StyledTableRowHistory key={rowIndex}>
                        <StyledTableCellHistory>
                          {row?.addiction_type}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.addiction_description}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.onset_datetime
                            ? getHumanReadableDiffInPastDateWithAgo(
                              row?.onset_datetime,
                              row
                            )
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.abatement_datetime
                            ? getHumanReadableDurationInDate(
                              row?.onset_datetime,
                              row?.abatement_datetime,
                              row
                            )
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>-</StyledTableCellHistory>
                      </StyledTableRowHistory>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {addictionHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Addiction Type
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Addiction Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {addictionHistoryData["new"].map((row, rowIndex) => {
                    console.log("Addiction Row", row); // Logging the row object

                    return (
                      <StyledTableRowHistory key={rowIndex}>
                        <StyledTableCellHistory>
                          {row?.addiction_type ?? "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.addiction_description ?? "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {normalizeLegacyDuration(
                            row?.onset_duration,
                            row?.onset_duration,
                            row?.onset_duration_type
                          )
                            ? `${normalizeLegacyDuration(
                              row?.onset_duration,
                              row?.onset_duration,
                              row?.onset_duration_type
                            )} ago`
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {normalizeLegacyDuration(
                            row?.abatement_duration,
                            row?.abatement_duration,
                            row?.abatement_duration_type
                          ) || "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {addictionHistoryData["prev"].includes(row) ? (
                            "-"
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <Tooltip title="Edit">
                                <OutlinedIconFab
                                  onClick={() =>
                                    handleAddictionEditClick(row, rowIndex)
                                  }
                                  sx={{ marginRight: 1 }}
                                >
                                  <Icon name="editInTable" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <OutlinedIconFab
                                  onClick={() =>
                                    handleAddictionDelete(rowIndex)
                                  }
                                >
                                  <Icon name="deleteInTable" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            </div>
                          )}
                        </StyledTableCellHistory>
                      </StyledTableRowHistory>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default AddictionHistory;
